import { TFunction } from 'react-i18next'
import type { ComboboxItem } from '@node-space/ui-components'
import { DEFAULT_ZONE_OFFSET } from 'constants/timezones'
import type { Limit } from 'types/paymentLimits'
import { LimitOwner, LimitType } from 'types/paymentLimits'

export const LIMIT_TYPE_LABELS = {
  TRANSACTION: 'paymentLimits.transactionLimit',
  DAILY: 'paymentLimits.dailyLimit',
} satisfies Record<Limit['limitType'], string>

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  SEPA_CT: 'SEPA',
  SEPA_INST: 'SEPA Instant',
  SWIFT: 'SWIFT',
  FASTER_PAYMENT: 'Faster Payment',
  ACH: 'ACH',
  FEDWIRE: 'Fedwire',
  FEDNOW: 'FedNow',
  CRNOW: 'CRNow',
  RTP: 'RTP',
}

export const CURRENCIES = ['EUR', 'GBP', 'USD'] as const

export const PAYMENT_METHOD_OPTIONS: ComboboxItem[] = Object.entries(PAYMENT_METHOD_LABELS).map(
  ([value, leftText]) => ({ value, leftText })
)

export const getPaymentMethodOptionsForCurrency = (currency: string): ComboboxItem[] => {
  switch (currency) {
    case 'EUR':
      return [
        { value: 'SEPA_CT', leftText: 'SEPA' },
        { value: 'SEPA_INST', leftText: 'SEPA Instant' },
        { value: 'SWIFT', leftText: 'SWIFT' },
      ]
    case 'GBP':
      return [
        { value: 'FASTER_PAYMENT', leftText: 'Faster Payment' },
        { value: 'SWIFT', leftText: 'SWIFT' },
      ]
    case 'USD':
      return [
        { value: 'ACH', leftText: 'ACH' },
        { value: 'FEDWIRE', leftText: 'Fedwire' },
        { value: 'CRNOW', leftText: 'CRNow' },
        { value: 'FEDNOW', leftText: 'FedNow' },
        { value: 'RTP', leftText: 'RTP' },
        { value: 'SWIFT', leftText: 'SWIFT/International Wire' },
      ]
    default:
      return []
  }
}

export const getLimitTypeOptions = (t: TFunction<'translation', undefined>) => [
  { value: LimitType.TRANSACTION, label: t('paymentLimits.transactionLimit') },
  { value: LimitType.DAILY, label: t('paymentLimits.dailyLimit') },
]

export const getAmountLabel = (
  limitType: string,
  t: TFunction<'translation', undefined>
): string => {
  if (limitType === LimitType.DAILY) return t('paymentLimits.dailyLimit')
  return t('paymentLimits.transactionLimit')
}

export const formatPaymentMethodLabel = (method: string): string => {
  return PAYMENT_METHOD_LABELS[method] ?? method.replaceAll('_', ' ')
}

export const OWNER_LABELS: Record<LimitOwner, string> = {
  [LimitOwner.INTERNAL_USER]: 'BVNK',
  [LimitOwner.ACCOUNT_USER]: 'Account',
  [LimitOwner.CUSTOMER_USER]: 'EPC',
}

export const formatOwnerLabel = (owner: string): string => {
  return OWNER_LABELS[owner as LimitOwner] ?? owner
}

export const OWNER_HIGHLIGHT_TYPES: Record<LimitOwner, 'feature' | 'neutral' | 'warning'> = {
  [LimitOwner.INTERNAL_USER]: 'feature',
  [LimitOwner.ACCOUNT_USER]: 'neutral',
  [LimitOwner.CUSTOMER_USER]: 'warning',
}

export const getOwnerHighlightType = (
  owner: string | undefined
): 'feature' | 'neutral' | 'warning' => {
  if (!owner) return 'warning'
  return OWNER_HIGHLIGHT_TYPES[owner as LimitOwner] ?? 'warning'
}

/**
 * Format zone offset for display
 * Converts "Z" to "UTC", normalizes offsets to "+HH:MM" format
 */
export const formatZoneOffset = (zoneOffset: string | undefined): string => {
  if (!zoneOffset) return DEFAULT_ZONE_OFFSET
  const trimmed = zoneOffset.trim()
  const upper = trimmed.toUpperCase()
  if (upper === 'Z' || upper === 'UTC') return DEFAULT_ZONE_OFFSET

  // Already in correct format like "+02:00" or "-05:00"
  if (/^[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed
  }

  // Normalize common variants like "+2", "-2", "+2:00"
  const normalized = normalizeZoneOffset(trimmed)
  if (!normalized || normalized === 'Z') return DEFAULT_ZONE_OFFSET
  return normalized
}

/**
 * Normalize zone offset input for API submission
 * Converts user-friendly inputs to API format
 */
export const normalizeZoneOffset = (input: string | undefined | null): string | null => {
  if (!input || input.trim() === '') return null

  const trimmed = input.trim()

  // Handle Z, UTC, or 0 -> send as "Z"
  if (trimmed === 'Z' || trimmed.toUpperCase() === 'UTC' || trimmed === '0') {
    return 'Z'
  }

  // Handle numeric inputs like "-2", "2", "+2"
  const numericMatch = trimmed.match(/^([+-]?)(\d{1,2})$/)
  if (numericMatch) {
    const sign = numericMatch[1] || '+'
    const hours = numericMatch[2].padStart(2, '0')
    return `${sign}${hours}:00`
  }

  // Handle partial formats like "-2:00", "+2:00"
  const partialMatch = trimmed.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (partialMatch) {
    const sign = partialMatch[1] || '+'
    const hours = partialMatch[2].padStart(2, '0')
    const minutes = partialMatch[3]
    return `${sign}${hours}:${minutes}`
  }

  // Return as-is if already in correct format or can't parse
  return trimmed
}

/**
 * Determines if a user can edit a specific payment limit based on:
 * - General payment limits permission
 * - Limit ownership (INTERNAL_USER limits require SCOPE_bvnk:limits:edit)
 *
 * Note: Customer/Account isolation (users can only edit their own limits) is
 * enforced by the backend. The frontend relies on the backend returning only
 * the limits the user is authorized to see.
 *
 * @param limit - The payment limit to check
 * @param canEditPaymentLimits - General permission to edit payment limits
 * @param userRoles - User's roles array from profile (includes scopes)
 * @returns true if the user can edit this specific limit
 */
export const canEditLimit = (
  limit: Limit,
  canEditPaymentLimits: boolean,
  userRoles?: string[]
): boolean => {
  if (!canEditPaymentLimits) {
    return false
  }

  if (limit.owner === LimitOwner.INTERNAL_USER) {
    return userRoles?.includes('SCOPE_bvnk:limits:edit') ?? false
  }

  // Unknown or missing owners default to read-only: a new owner type could be
  // BVNK-controlled, so it must not bypass the scope check above.
  return limit.owner === LimitOwner.ACCOUNT_USER || limit.owner === LimitOwner.CUSTOMER_USER
}
